import { RootState, runAppSaga } from 'app/state/store'
import {
  BuilderAgentResourceState,
  KeplerLayer,
  BuilderAgentDataset,
  LayerType,
  BuilderAgentAddSourceAction,
  BuilderAgentRemoveSourceAction,
  BuilderAgentAddLayerAction,
  BuilderAgentRemoveLayerAction,
  BuilderAgentSetLayerStyleAction,
  MapDatasetEnriched,
  BuilderAgentAuthor,
  BuilderAgentConversationItem,
  BuilderAgentLayer,
  BuilderAgentAddMarkerAction,
  KeplerTableField,
  KeplerLayerConfig,
  PopupEvent,
  PopupStyle,
  PopupFormatSettings,
  BuilderAgentDatasetConfig,
  PopupSettings,
  KeplerScaleType,
  KeplerAggregationType,
  BuilderAgentActionTypes,
  BuilderAgentAsyncWorkflowJobCheckResultsAction,
  BuilderAgentSetSpatialFilterAction
} from '../types'
import { point as turfPoint, featureCollection } from '@turf/helpers'
import bbox from '@turf/bbox'
import { all, put, putResolve } from 'redux-saga/effects'
import {
  addAgentThreadDataset,
  addAgentThreadLayer,
  addLocationsToUserMessage,
  addLayerToExistingDatasetRequest,
  builderAgentActionToExecute,
  getEnrichedDatasetById,
  loadAttributeStatsRequest,
  removeAgentThreadDataset,
  removeAgentThreadLayer,
  removeAllAgentConversationLocations,
  setAgentThread,
  setLayerPopupSettings,
  setPopupSettings,
  updateAgentThreadDataset,
  updateAgentThreadLayer
} from '../state/builderSlice'
import { appStoreDispatch, appStoreSelect, waitForSelectorValue } from 'app/state/storeUtils'
import {
  layerConfigChange,
  layerVisualChannelConfigChange,
  removeDataset,
  removeLayer,
  reorderLayer,
  updateMap
} from '@carto/kepler.gl/actions'
import { Formats, MapType } from 'features/maps/types'
import { getLayerLoader } from '../state/builderSagas'
import { hexToRGB, isValidHex } from 'features/common/utils/colors'
import { KeplerColorRange } from '../types'
import { clamp } from 'features/common/utils/clamp'
import { RequestStatus } from 'features/common'
import { addSpatialFilter, removeSpatialFilter } from '@carto/react-redux'
import { fitBounds } from '../utils/mapUtils'
import { GENAI_LOCATION_ZOOM_LEVEL_ON_ADD } from '../utils/genAiLocationUtils'
import { MAXIMUM_ZOOM_LEVEL } from 'features/common/ui/Map/ZoomControl'
import { getDefaultQualitativeColors, getDefaultSequentialColors } from '../ui/theme/cartoColors'
import { isInRange } from 'features/common/utils/isInRange'
import {
  AGGREGATION_TYPES_NUMERIC,
  AGGREGATION_TYPES_STRING,
  FIELD_TYPES_NUMERIC,
  MAX_COLOR_STEPS,
  MIN_COLOR_STEPS
} from '../ui/builderConstants'
import { MAX_HOVER_FIELDS } from '../ui/builderConstants'
import { getDatasetConfigForAgent, isDynamicSpatialIndexDataset } from '../state/builderSliceUtils'
import { queryClient } from 'queryClient'
import { JobStatusType } from 'features/jobs/types'
import { JOB_STATUS_QUERY_KEY } from '../hooks/genAiActionsHooks/useJobStatusPolling'
import { getAgent } from 'features/maps/state/mapsSlice'
import { isProviderType } from 'features/connections'
import { deleteProcessingJob } from 'features/jobs/api/processingJobs'

export function getAgentLayerIds(): string[] {
  const agentLayers: BuilderAgentLayer[] = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
  return agentLayers.map((d) => d.layerId).filter(Boolean) as string[]
}

export function getAgentDatasetIds(): string[] {
  const agentDatasets: BuilderAgentDataset[] = appStoreSelect(
    (state: RootState) => state.builder.genAiThread?.datasets || []
  )
  return agentDatasets.map((d) => d.datasetId).filter(Boolean) as string[]
}

export function isAgentLayer(layerId: string): boolean {
  return getAgentLayerIds().includes(layerId)
}

export function isAgentDataset(datasetId: string): boolean {
  return getAgentDatasetIds().includes(datasetId)
}

export async function deleteAgentConversation(): Promise<void> {
  await removeAllAgentLayers()
  await removeAllAgentDatasets()

  appStoreDispatch(setAgentThread({}))
}

export async function deleteAgentJobs(): Promise<void> {
  const currentActionToExecute = appStoreSelect(builderAgentActionToExecute)
  if (
    !currentActionToExecute ||
    currentActionToExecute.action !== BuilderAgentActionTypes.AsyncWorkflowJobCheckResults
  ) {
    return
  }

  const jobs = currentActionToExecute as BuilderAgentAsyncWorkflowJobCheckResultsAction
  const jobsToDelete =
    jobs.params?.jobs?.map((job) => {
      return {
        connectionName: job.connectionName,
        jobId: job.jobId
      }
    }) || []

  // Set job status to cancelled in the query cache before deleting
  jobsToDelete.forEach((job) => {
    queryClient.setQueryData([JOB_STATUS_QUERY_KEY, job.connectionName, job.jobId], { status: JobStatusType.Cancelled })
  })

  await Promise.all(
    jobsToDelete.map((job) =>
      deleteProcessingJob({
        connection: job.connectionName,
        jobId: job.jobId
      })
    )
  )
}

export async function interruptAgentConversation(): Promise<void> {
  const prevConversation = appStoreSelect((state: RootState) => state.builder.genAiConversation)
  const prevThread = appStoreSelect((state: RootState) => state.builder.genAiThread)

  const lastResponseIndex = prevConversation.findLastIndex((item) => {
    switch (item.author) {
      case BuilderAgentAuthor.System:
        return item
      case BuilderAgentAuthor.Bot:
        // API bundles 'response' and 'action_frontend' into one event, with
        // optional message. Skip if no message.
        return item.type === 'response' && item.message
      default:
        return false
    }
  })
  const lastResponse = lastResponseIndex >= 0 ? prevConversation[lastResponseIndex] : undefined
  const lastParent = (lastResponse as { parent?: string })?.parent

  const conversation = prevConversation.slice(0, lastResponseIndex + 1)
  const thread = prevThread && lastParent ? { ...prevThread, parent: lastParent } : undefined

  // TODO(sc-507885): Remove any datasets or layers created by the conversation items being removed.
  appStoreDispatch(setAgentThread({ thread, conversation }))
}

export type AgentActionRequest = { actionId: string }

export type AgentAddDatasetRequest = AgentActionRequest & BuilderAgentAddSourceAction['params']
export type AgentAddDatasetResponse = BuilderAgentDatasetConfig

export async function addAgentDataset(request: AgentAddDatasetRequest): Promise<AgentAddDatasetResponse> {
  const { actionId, connection, provider, source, type, geoColumn } = request

  try {
    const threadDataset: BuilderAgentDataset = { actionId, state: BuilderAgentResourceState.Loading }

    appStoreDispatch(addAgentThreadDataset(threadDataset))

    const mapId = appStoreSelect((state: RootState) => state.maps.currentMap?.id as string)
    const accessToken = appStoreSelect(getAgent)?.token

    assertAsAgent(isProviderType(provider), `Unsupported provider, "${provider}"`)
    assertAsAgent(type in MapType, `Unsupported source type, "${type}"`)

    const datasetId = await runAppSaga(function* () {
      let columns: string[] | undefined

      const loader = getLayerLoader({
        accessToken,
        mapId,
        connection,
        provider,
        source,
        type,
        columns,
        geoColumn,
        format: Formats.TILEJSON,
        createLayer: false
      })

      const datasetId = loader.datasetId

      // Link agent and dataset before calling .add(). In addition to hiding the dataset in the UI,
      // this prevents the dataset from being saved — which would overwrite 'datasetId'.
      yield put(
        updateAgentThreadDataset({
          actionId,
          dataset: { state: BuilderAgentResourceState.Loading, datasetId }
        })
      )

      yield loader.add()
      yield loader.getKeplerDataset()

      return datasetId
    })

    await waitForSelectorValue(
      (state) => state.maps.currentMapDatasets.some((d) => d.id === datasetId && d.status !== RequestStatus.Pending),
      true
    )

    // Datasets may be added, but in an error state, for example if the SQL query contains an error.
    const dataset = appStoreSelect((state) => state.maps.currentMapDatasets.find((d) => d.id === datasetId))
    if (dataset?.status !== RequestStatus.Fulfilled) {
      appStoreDispatch(removeDataset(datasetId))
      throw new AgentError(dataset?.errorMessage || 'Source creation failed')
    }

    // [sc-513652] Ordinarily Builder handles datasets without a specified "geoColumn", but the AI Agent
    // cannot, and it may indicate an error in a SQL query or workflow.
    const datasetEnriched: MapDatasetEnriched = appStoreSelect((state) => getEnrichedDatasetById(state, datasetId))
    if (!datasetEnriched.metadata.tilejson) {
      appStoreDispatch(removeDataset(datasetId))
      throw new AgentError(`Source creation failed, no map tiles found. Verify schema and "geoColumn" name?`)
    }

    appStoreDispatch(
      updateAgentThreadDataset({
        actionId,
        dataset: { state: BuilderAgentResourceState.Success, datasetId }
      })
    )

    return getDatasetConfigForAgent(datasetEnriched)
  } catch (e) {
    console.error(e)

    appStoreDispatch(
      updateAgentThreadDataset({
        actionId,
        dataset: { state: BuilderAgentResourceState.Error, error: 'Source creation failed' }
      })
    )

    throw e
  }
}

export type AgentRemoveDatasetRequest = Partial<AgentActionRequest> & BuilderAgentRemoveSourceAction['params']
export type AgentRemoveDatasetResponse = void

export async function removeAgentDataset({ datasetId }: AgentRemoveDatasetRequest): Promise<void> {
  try {
    const datasets = appStoreSelect((state) => state.maps.currentMapDatasets)
    const threadDatasets = appStoreSelect((state) => state.builder.genAiThread?.datasets || [])
    const isThreadDataset = threadDatasets.some((td) => td.datasetId === datasetId)

    assertAsAgent(
      datasets.some((d) => d.id === datasetId),
      `No source found with ID, "${datasetId}"`
    )
    assertAsAgent(isThreadDataset, `Cannot remove non-AI dataset, "${datasetId}"`)

    await runAppSaga(function* () {
      yield put(removeDataset(datasetId))
    })

    // Ensure dataset is associated with AI Agent until it is fully removed.
    await runAppSaga(function* () {
      yield put(removeAgentThreadDataset({ datasetId }))
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

/** Internal-only; needs actionId if exposed to agent. */
async function removeAllAgentDatasets(): Promise<void> {
  const datasets = appStoreSelect((state) => state.maps.currentMapDatasets)
  const threadDatasets = appStoreSelect((state) => state.builder.genAiThread?.datasets || [])

  try {
    await runAppSaga(function* () {
      yield all(
        threadDatasets.map(function* ({ datasetId }) {
          if (datasetId && datasets.some((d) => d.id === datasetId)) {
            yield put(removeDataset(datasetId))
          }
        })
      )
    })

    // Ensure dataset is associated with AI Agent until it is fully removed.
    await runAppSaga(function* () {
      yield all(
        threadDatasets.map(function* ({ datasetId }) {
          if (datasetId) {
            yield put(removeAgentThreadDataset({ datasetId }))
          }
        })
      )
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

export type AgentAddLayerRequest = AgentActionRequest & BuilderAgentAddLayerAction['params']
export type AgentAddLayerResponse = { layerId: string; type: LayerType }

export async function addAgentLayer(request: AgentAddLayerRequest): Promise<AgentAddLayerResponse> {
  const { actionId, datasetId, label, hoverColumns, hoverColumnsAggregation = {} } = request

  let layerId: string | undefined

  try {
    // Validate popup settings before creating layer, to save time and create/delete churn.
    const needsHoverPopup = hoverColumns && hoverColumns?.length > 0
    if (needsHoverPopup) {
      validateAgentLayerPopupSettings({ datasetId, hoverColumns, hoverColumnsAggregation })
    }

    appStoreDispatch(addAgentThreadLayer({ actionId, state: BuilderAgentResourceState.Loading }))

    // Execution must block until layer is added.
    await runAppSaga(function* () {
      yield putResolve(addLayerToExistingDatasetRequest(datasetId))
    })

    // By default, new layers are appended last.
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layerOrder: number[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layerOrder)
    const layer = layers[layers.length - 1]

    assertAsAgent(layer, 'Something went wrong while creating the layer')

    layerId = layer.id

    // Mark as AI-created layer, _then_ update label. LayerPanelHeader keeps
    // internal state, and tries to change label back, but component is not mounted
    // for layers once marked as AI-generated. [sc-506406].
    await runAppSaga(function* () {
      yield put(updateAgentThreadLayer({ actionId, layer: { state: BuilderAgentResourceState.Success, layerId } }))
    })

    await runAppSaga(function* () {
      // Reorder so new layer appears first.
      yield put(reorderLayer([layers.length - 1, ...layerOrder.slice(0, -1)]))
      yield put(layerConfigChange(layer, { label }))
    })

    // Configure hover popup if hoverColumns provided.
    if (needsHoverPopup) {
      await setAgentLayerPopupSettings({
        layerId,
        datasetId,
        hoverColumns,
        hoverColumnsAggregation
      })
    }

    return { layerId, type: layer.type }
  } catch (e) {
    console.error(e)

    if (layerId) {
      const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
      const layerIdx = layers.findIndex((layer) => layer.id === layerId)
      if (layerIdx !== -1) {
        await runAppSaga(function* () {
          yield put(removeLayer(layerIdx))
        })
      }
    }

    appStoreDispatch(
      updateAgentThreadLayer({
        actionId,
        layer: { state: BuilderAgentResourceState.Error, error: 'Layer creation failed' }
      })
    )

    throw e
  }
}

type AgentLayerPopupSettings = Required<
  Pick<AgentAddLayerRequest, 'datasetId' | 'hoverColumns' | 'hoverColumnsAggregation'>
>

function validateAgentLayerPopupSettings({
  datasetId,
  hoverColumns,
  hoverColumnsAggregation
}: AgentLayerPopupSettings): void {
  const dataset: MapDatasetEnriched = appStoreSelect((state: RootState) => getEnrichedDatasetById(state, datasetId))
  assertAsAgent(dataset, `No source found with ID, "${datasetId}"`)

  const invalidColumns = hoverColumns.filter((id) => !dataset.fields.some((f) => f.id === id))
  assertAsAgent(invalidColumns.length === 0, `Unknown columns: ${invalidColumns.join(', ')}`)

  assertAsAgent(
    !isDynamicSpatialIndexDataset(dataset) || hoverColumns.every((col) => hoverColumnsAggregation?.[col]),
    'Aggregated source requires "hoverColumnsAggregation" for all "hoverColumns"'
  )
}

async function setAgentLayerPopupSettings({
  layerId,
  hoverColumns,
  hoverColumnsAggregation
}: { layerId: string } & AgentLayerPopupSettings): Promise<void> {
  const hoverConfig: PopupFormatSettings = {
    // [sc-506551] Given more than MAX_HOVER_FIELDS columns, ignore the rest without error.
    fields: hoverColumns.slice(0, MAX_HOVER_FIELDS).map((name) => ({
      name,
      spatialIndexAggregation: hoverColumnsAggregation?.[name]
    })),
    style: PopupStyle.Light,
    templateMode: false
  }

  try {
    await runAppSaga(function* () {
      yield put(
        setLayerPopupSettings({
          layerId,
          settings: {
            enabled: true,
            [PopupEvent.Hover]: hoverConfig
          }
        })
      )
    })
  } catch (e) {
    console.error('Failed to configure hover popup:', e)
    throw new AgentError('Failed to configure hover popup')
  }
}

export type AgentRemoveLayerRequest = Partial<AgentActionRequest> & BuilderAgentRemoveLayerAction['params']
export type AgentRemoveLayerResponse = void

export async function removeAgentLayer({ layerId }: AgentRemoveLayerRequest): Promise<void> {
  try {
    const threadLayers = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layerIdx = layers.findIndex((layer) => layer.id === layerId)
    const isThreadLayer = threadLayers.some((tl) => tl.layerId === layerId)

    assertAsAgent(layerIdx !== -1, `No layer found with ID, "${layerId}"`)
    assertAsAgent(isThreadLayer, `Cannot remove non-AI layer, "${layerId}"`)

    // Remove any popup settings for the removed layer.
    const popupSettings = appStoreSelect((state: RootState) => state.builder.popupSettings)
    let updatedPopupSettings: PopupSettings | undefined
    if (popupSettings?.layers?.[layerId]) {
      updatedPopupSettings = { ...popupSettings, layers: { ...popupSettings.layers } }
      delete updatedPopupSettings.layers[layerId]
    }

    await runAppSaga(function* () {
      yield put(removeLayer(layerIdx))
      yield put(removeAgentThreadLayer({ layerId }))

      if (updatedPopupSettings) {
        yield put(setPopupSettings(updatedPopupSettings))
      }
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

/** Internal-only; needs actionId if exposed to agent. */
async function removeAllAgentLayers(): Promise<void> {
  try {
    const agentLayers = appStoreSelect((state) => state.builder.genAiThread?.layers || [])

    // Remove layers sequentially, layerIdx may change mid-operation.
    // TODO: After Kepler removal, consider refactoring removeLayer to use ID, not index.
    for (const { layerId } of agentLayers) {
      const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
      const layerIdx = layers.findIndex((layer) => layer.id === layerId)
      if (!layerId || layerIdx === -1) continue

      await removeAgentLayer({ layerId })
    }
  } catch (e) {
    console.error('_removeAllAgentLayers', e)
    throw e
  }
}

export type AgentSetSpatialFilterRequest = AgentActionRequest & BuilderAgentSetSpatialFilterAction['params']
export type AgentSetSpatialFilterResponse = void

export async function setAgentSpatialFilter(request: AgentSetSpatialFilterRequest): Promise<void> {
  const { geometry } = request

  try {
    if (!geometry) {
      appStoreDispatch(removeSpatialFilter())
      return
    }

    const mapState = appStoreSelect((state: RootState) => state.keplerGl.map.mapState)

    appStoreDispatch(addSpatialFilter({ geometry: { type: 'Feature' as const, geometry, properties: {} } }))

    // Auto-zoom to the mask extent
    try {
      const [xmin, ymin, xmax, ymax] = bbox(geometry)
      const { latitude, longitude, zoom } = fitBounds({
        extent: { xmin, ymin, xmax, ymax },
        mapState
      })
      const clampedZoom = clamp(zoom, mapState.minZoom ?? 0, mapState.maxZoom || MAXIMUM_ZOOM_LEVEL)
      appStoreDispatch(updateMap({ latitude, longitude, zoom: clampedZoom }))
    } catch (error) {
      console.warn('Failed to auto-zoom to mask extent:', error)
      // Non-critical error - mask is already added, just zoom failed
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

/**
 * Remove all map context from the AI conversation.
 * This includes all AI-generated layers, datasets, and locations.
 */
export async function removeAllMapContext(): Promise<void> {
  try {
    // 1. Remove all AI-generated layers (includes cleanup of metadata and popup settings)
    await removeAllAgentLayers()

    // 2. Remove all AI-generated datasets (includes cleanup of metadata)
    await removeAllAgentDatasets()

    // 3. Mark all locations as removed (soft delete)
    appStoreDispatch(removeAllAgentConversationLocations())
  } catch (e) {
    console.error('removeAllMapContext', e)
    throw e
  }
}

export type AgentSetLayerStyleRequest = AgentActionRequest & BuilderAgentSetLayerStyleAction['params']
export type AgentSetLayerStyleResponse = void

const DEFAULT_AGENT_LAYER_CONFIG = {
  color: [255, 255, 255, 255],
  colorField: undefined,
  colorScale: undefined,
  radiusField: undefined,
  visConfig: {
    opacity: 0.8,
    filled: true,
    colorRange: undefined,
    colorAggregation: undefined,
    stroked: false,
    strokeOpacity: 0.8,
    strokeColor: [255, 255, 255, 255],
    thickness: 1,
    radius: undefined,
    radiusRange: undefined
  }
}

export async function setLayerStyle(request: AgentSetLayerStyleRequest): Promise<void> {
  const { layerId, ...style } = request

  try {
    const threadLayers = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layer = layers.find((layer) => layer.id === layerId)
    const isThreadLayer = threadLayers.some((tl) => tl.layerId === layerId)

    assertAsAgent(layer, `No layer found with ID, "${layerId}"`)
    assertAsAgent(isThreadLayer, `Cannot change non-AI layer, "${layerId}"`)

    const datasetId = layer.config.dataId
    const dataset: MapDatasetEnriched = appStoreSelect((state: RootState) => getEnrichedDatasetById(state, datasetId))

    const config: KeplerLayerConfig = {
      ...layer.config,
      visConfig: { ...layer.config.visConfig }
    }

    const attributes = new Set<string>()
    const visualChannels = new Set<string>()

    // Despite tool instructions, agent often returns an empty
    // string for parameters instead of 'undefined'. Handle both.

    if (style.opacity !== undefined) {
      config.visConfig.opacity = clamp(style.opacity, 0.0, 1.0)
      config.visConfig.strokeOpacity = clamp(style.opacity, 0.0, 1.0)
    }

    if (style.fillColor) {
      config.color = hexToRGB(style.fillColor)
      config.colorField = DEFAULT_AGENT_LAYER_CONFIG.colorField
      config.colorScale = DEFAULT_AGENT_LAYER_CONFIG.colorScale
      config.visConfig.colorRange = DEFAULT_AGENT_LAYER_CONFIG.visConfig.colorRange
      config.visConfig.colorAggregation = DEFAULT_AGENT_LAYER_CONFIG.visConfig.colorAggregation
    } else if (style.fillColorColumn) {
      const colorField = dataset.fields.find((f) => f.id === style.fillColorColumn)
      assertAsAgent(colorField, `No field with name, "${style.fillColorColumn}"`)

      config.color = DEFAULT_AGENT_LAYER_CONFIG.color
      config.colorField = colorField
      config.colorScale = getLayerScaleForField(dataset, colorField)
      config.visConfig.colorRange = getLayerColorRange(dataset, colorField, style.fillColorPalette)
      config.visConfig.colorAggregation = isDynamicSpatialIndexDataset(dataset)
        ? getLayerFieldAggregation(colorField, 'fillColorColumn', style.fillColorColumnAggregation)
        : DEFAULT_AGENT_LAYER_CONFIG.visConfig.colorAggregation

      attributes.add(colorField.id)
      visualChannels.add('color')
    }

    if (style.outlineWidth !== undefined) {
      config.visConfig.stroked = style.outlineWidth > 0
      config.visConfig.thickness = clamp(style.outlineWidth, 0, 50) // Must match Latitude.
    }

    if (style.outlineColor) {
      config.visConfig.strokeColor = hexToRGB(style.outlineColor)
    }

    if (style.radius !== undefined) {
      // Agent frequently returns radius=0 for non-point layers.
      assertAsAgent(layer.meta.featureTypes?.point || style.radius === 0, 'Only point layers support "radius"')
      config.radiusField = DEFAULT_AGENT_LAYER_CONFIG.radiusField
      config.visConfig.radius = clamp(style.radius, 0, 50) // Must match Latitude.
      config.visConfig.radiusRange = DEFAULT_AGENT_LAYER_CONFIG.visConfig.radiusRange
    } else if (style.radiusColumn) {
      const radiusField = dataset.fields.find((f) => f.id === style.radiusColumn)
      assertAsAgent(layer.meta.featureTypes?.point, 'Only point layers support "radiusColumn"')
      assertAsAgent(radiusField, `No field with name, "${style.radiusColumn}"`)
      assertAsAgent(FIELD_TYPES_NUMERIC.includes(radiusField.type), '"radiusColumn" must be numeric')

      config.radiusField = radiusField
      config.visConfig.radius = DEFAULT_AGENT_LAYER_CONFIG.visConfig.radius
      config.visConfig.radiusRange = [0, 50]

      attributes.add(radiusField.id)
      visualChannels.add('radius')
    }

    // Load attribute stats _before_ adding color scales.
    if (attributes.size > 0) {
      await runAppSaga(function* () {
        yield all(Array.from(attributes).map((attribute) => put(loadAttributeStatsRequest({ datasetId, attribute }))))
      })
      await waitForSelectorValue((state) => !!state.builder.datasetAttributeStatsOnRequest[datasetId], false)
    }

    await runAppSaga(function* () {
      yield put(layerConfigChange(layer, config))
      yield all(
        Array.from(visualChannels).map((channel) => put(layerVisualChannelConfigChange(layer, config, channel)))
      )
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

function getLayerColorRange(dataset: MapDatasetEnriched, field: KeplerTableField, colors?: string[]): KeplerColorRange {
  if (colors?.length) {
    const min = MIN_COLOR_STEPS
    const max = MAX_COLOR_STEPS
    assertAsAgent(
      isInRange(colors.length, min, max),
      `Color palette must contain ${min}–${max} colors, got ${colors.length}`
    )
    for (const color of colors) {
      assertAsAgent(isValidHex(color), `Invalid hex color format: "${color}". Expected format: #RRGGBB`)
    }
    return { name: 'Custom Palette', category: 'Custom', type: 'custom', colors }
  }

  const isNumeric = FIELD_TYPES_NUMERIC.includes(field.type)
  const colorRange: KeplerColorRange = isNumeric ? getDefaultSequentialColors() : getDefaultQualitativeColors()

  // Spatial index aggregated fields default to log scale, see `LayerPanel.tsx`.
  if (isNumeric && isDynamicSpatialIndexDataset(dataset)) {
    colorRange.uiCustomScaleType = 'logarithmic'
  }

  return colorRange
}

function getLayerScaleForField(dataset: MapDatasetEnriched, field: KeplerTableField): KeplerScaleType {
  if (FIELD_TYPES_NUMERIC.includes(field.type)) {
    // Spatial index aggregated fields default to log scale (Custom), see `LayerPanel.tsx`.
    return isDynamicSpatialIndexDataset(dataset) ? KeplerScaleType.Custom : KeplerScaleType.Quantile
  }
  return KeplerScaleType.Ordinal
}

function getLayerFieldAggregation(
  field: KeplerTableField,
  fieldLabel: string,
  fieldAggregation: KeplerAggregationType | undefined
): KeplerAggregationType | undefined {
  assertAsAgent(fieldAggregation, `Styling by "${fieldLabel}" on aggregated source requires column aggregation`)

  if (FIELD_TYPES_NUMERIC.includes(field.type)) {
    assertAsAgent(
      AGGREGATION_TYPES_NUMERIC.includes(fieldAggregation),
      `Unsupported aggregation for "${fieldLabel}", allowed operators: ${AGGREGATION_TYPES_NUMERIC.join(', ')}`
    )
  } else if (field.type === 'string') {
    assertAsAgent(
      AGGREGATION_TYPES_STRING.includes(fieldAggregation),
      `Unsupported aggregation for "${fieldLabel}", allowed operators: ${AGGREGATION_TYPES_STRING.join(', ')}`
    )
  } else {
    throw new AgentError(`Aggregation on "${fieldLabel}" (type: "${field.type}") not supported`)
  }

  return fieldAggregation
}

export type AgentAddMarkerRequest = AgentActionRequest & BuilderAgentAddMarkerAction['params']
export type AgentAddMarkerResponse = Record<string, never>

export async function addAgentMarker(request: AgentAddMarkerRequest): Promise<AgentAddMarkerResponse> {
  const { markers } = request

  assertAsAgent(markers, 'Markers array is required')
  assertAsAgent(Array.isArray(markers), 'Markers must be an array')
  assertAsAgent(markers.length > 0, 'Markers array must contain at least one marker')

  // Build geometries and locations array during validation (single pass)
  const geometries: GeoJSON.Point[] = []
  const locations: Array<{ geometry: GeoJSON.Point; label: string; removed: boolean }> = []

  // Validate each marker and construct geometry
  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i]
    const markerPrefix = `Marker ${i + 1}`

    // Construct GeoJSON.Point from latitude and longitude
    const geometry: GeoJSON.Point = {
      type: 'Point',
      coordinates: [marker.longitude, marker.latitude]
    }

    // Validate with Turf
    try {
      turfPoint([marker.longitude, marker.latitude])
    } catch (e) {
      throw new AgentError(`${markerPrefix}: Invalid GeoJSON Point: ${e instanceof Error ? e.message : String(e)}`)
    }

    // Store geometry and location
    geometries.push(geometry)
    locations.push({ geometry, label: marker.label, removed: false })
  }

  // Get conversation and find the last user message
  const conversation = appStoreSelect((state: RootState) => state.builder.genAiConversation)
  const lastUserMessage = conversation.findLast((item) => item.author === BuilderAgentAuthor.User)

  assertAsAgent(lastUserMessage, 'No user message found to associate markers with')
  assertAsAgent(lastUserMessage.author === BuilderAgentAuthor.User, 'Last message is not a user message')

  // Add locations to the user message
  appStoreDispatch(
    addLocationsToUserMessage({
      date: (lastUserMessage as Extract<BuilderAgentConversationItem, { author: BuilderAgentAuthor.User }>).date,
      locations
    })
  )

  // Auto-center map on markers
  if (geometries.length === 1) {
    // Single marker: center with fixed zoom level of 10
    const [lng, lat] = geometries[0].coordinates
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      appStoreDispatch(updateMap({ latitude: lat, longitude: lng, zoom: GENAI_LOCATION_ZOOM_LEVEL_ON_ADD }))
    }
  } else if (geometries.length > 1) {
    // Multiple markers: fit bounds to show all
    const points = geometries.map((g) => turfPoint(g.coordinates))
    const collection = featureCollection(points)
    const [xmin, ymin, xmax, ymax] = bbox(collection)

    const mapState = appStoreSelect((state: RootState) => state.keplerGl.map.mapState)
    const { latitude, longitude, zoom } = fitBounds({
      extent: { xmin, ymin, xmax, ymax },
      mapState
    })
    const clampedZoom = clamp(zoom, mapState.minZoom, mapState.maxZoom || MAXIMUM_ZOOM_LEVEL)
    if (Number.isFinite(latitude) && Number.isFinite(longitude) && Number.isFinite(clampedZoom)) {
      appStoreDispatch(updateMap({ latitude, longitude, zoom: clampedZoom }))
    }
  }

  return {}
}

/** Asserts the condition or throws an {@link AgentError}. */
function assertAsAgent(condition: unknown, msg: string): asserts condition {
  if (!condition) {
    throw new AgentError(msg)
  }
}

/**
 * Subclass for errors the Agent should receive verbatim. Errors not explicitly
 * thrown or wrapped as {@link AgentError} will be shown to the Agent only as a
 * generic "something went wrong"-style message.
 */
export class AgentError extends Error {}
